import React, { useState } from "react";
import { Users, AlertCircle } from "lucide-react";
import { useApp } from "../components/AppContext";
import { MainLayout } from "../components/MainLayout";
import { useCreateGroup } from "../api/groups/useCreateGroup";
import { useAppRouter } from "../hooks/useAppRouter";
import { useTranslation } from "../i18n/TranslationContext";

export function GroupCreate() {
	const { navigateDirectly, userProfile, updateUserCompletionState } = useApp();
	const router = useAppRouter();
	const { t } = useTranslation();
	const {
		createGroup,
		loading: apiLoading,
		error: apiError,
	} = useCreateGroup();

	const [formData, setFormData] = useState({
		name: "",
		description: "",
		privacy: "public" as "public" | "private",
	});

	const [error, setError] = useState<string>("");

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		if (error) setError(""); // Clear error when user types
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.name.trim()) {
			setError(t("groupCreate.errors.nameRequired"));
			return;
		}

		if (formData.name.length > 30) {
			setError(t("groupCreate.errors.nameTooLong"));
			return;
		}

		if (formData.description.length > 100) {
			setError(t("groupCreate.errors.descriptionTooLong"));
			return;
		}

		setError("");

		const result = await createGroup({
			name: formData.name,
			description: formData.description || undefined,
		});

		if (result) {
			// Update completion state
			updateUserCompletionState({ group_member: true });

			// Navigate to group display screen
			router.navigate("app-group");
		} else if (apiError) {
			setError(apiError);
		}
	};

	const handleBack = () => {
		navigateDirectly("app-group-select");
	};

	return (
		<MainLayout showBack={true} onBack={handleBack}>
			<div className="space-y-6">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="w-16 h-16 bg-[var(--ucm-primary-teal)] rounded-full flex items-center justify-center mx-auto mb-4">
						<Users size={32} className="text-white" />
					</div>
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-3">
						{t("groupCreate.title")}
					</h1>
					<p className="text-[var(--ucm-text-medium)] px-4">
						{t("groupCreate.subtitle")}
					</p>
				</div>

				{/* Create Group Form */}
				<form onSubmit={handleSubmit} className="space-y-6">
					<div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
						{/* Group Name */}
						<div className="mb-6">
							<label
								htmlFor="groupName"
								className="block text-sm font-medium text-[var(--ucm-text-medium)] mb-2"
							>
								{t("groupCreate.groupName")} *
							</label>
							<input
								type="text"
								id="groupName"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
								className="w-full px-4 py-3 border border-[var(--ucm-border-light)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] transition-colors"
								placeholder={t("groupCreate.groupNamePlaceholder")}
								maxLength={30}
								disabled={apiLoading}
							/>
							<div className="flex justify-between items-center mt-1">
								<span className="text-xs text-[var(--ucm-text-light)]">
									{t("groupCreate.groupNameHint")}
								</span>
								<span className="text-xs text-[var(--ucm-text-light)]">
									{formData.name.length}/30
								</span>
							</div>
						</div>
					</div>

					{/* Error Display */}
					{error && (
						<div className="flex items-start space-x-3 p-4 bg-[var(--ucm-error-red)] bg-opacity-10 border border-[var(--ucm-error-red)] rounded-xl">
							<AlertCircle
								size={16}
								className="text-[var(--ucm-error-red)] mt-0.5 flex-shrink-0"
							/>
							<p className="text-sm text-[var(--ucm-error-red)]">{error}</p>
						</div>
					)}

					{/* Form Actions */}
					<div className="flex space-x-3">
						<button
							type="button"
							onClick={handleBack}
							disabled={apiLoading}
							className="flex-1 h-12 bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-medium)] font-semibold rounded-full hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{t("common.cancel")}
						</button>
						<button
							type="submit"
							disabled={apiLoading || !formData.name.trim()}
							className="flex-1 h-12 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{apiLoading ? t("groupCreate.creating") : t("groupCreate.createGroup")}
						</button>
					</div>
				</form>

				{/* Information */}
				<div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-2xl p-4">
					<div className="flex items-start space-x-3">
						<span className="text-xl flex-shrink-0">ℹ️</span>
						<div>
							<h4 className="font-semibold text-[var(--ucm-dark-blue)] mb-1">
								{t("groupCreate.groupInformationTitle")}
							</h4>
							<ul className="text-sm text-[var(--ucm-dark-blue)] space-y-1">
								<li>• {t("groupCreate.maxMembersInfo")}</li>
								<li>• {t("groupCreate.leaderPrivilegesInfo")}</li>
								<li>• {t("groupCreate.shareGroupNumberInfo")}</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</MainLayout>
	);
}
